// CubeTestReport.js
import React from 'react';
import { useState, useEffect ,useMemo} from "react";
import Swal from "sweetalert2";
import { Row, Col, Button } from "reactstrap";
import Sidebar from '../../Sidebar';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import TableRowsCubeTestReport from "./TableRowsCubeTestReport";
import FloatingControls from '../../components/FloatingControls';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getCubeTestReportNumber, createCubeTestReport, getPendingCubeTestReports, closePendingCubeTestReport } from '../../services/CubeTestReportServices';
import { getDeliveryChallanListForCTR } from '../../services/DeliveryChallanServices';
import NumberSetting from '../../components/NumberSetting';
import { displayError } from '../../helpers';
import { getDisplayDate } from '../../utils/DateUtils';
import ReactTablePopUpSubmitWrapper from "../../components/ReactTablePopUpSubmitWrapper";
import CubeTestReportSelectionBanner from './CubeTestReportSelectionBanner';
import CubeTestReportAgePanel from './CubeTestReportAgePanel';
import {
  showDcGroupPop,
  getCubeStrengthResult,
  buildCubeIdFromSelection,
  buildPendingAgeRow,
  syncCubeIdsForAgeRows,
  applyAgeRowFieldChange,
  castingDateInputFromApi,
  reportDateFromCastingAndAge,
  resolveNextAgeDays,
  resolveCuredPlaceFromAgeRows,
  buildReportListPayload,
  mergeCsrRowsForAge,
  generateAndMergeCsrRowsForAge,
} from './CubeTestReportUtils';
import {
  buildPendingListTitle,
  decoratePendingRecords,
  getInitialCubeTestReportFormState,
  getPendingColumns,
  getPendingQueryRange,
  mapAllowedPlants,
  pickDefaultPlantId,
} from './cubeTestReportDefaults';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import { GradientCard } from '../../components/ListPageGradientSummary';
import { buildGradientCard } from '../../utils/listSummaryUtils';

function CubeTestReport() 
{
  const initialFormState = getInitialCubeTestReportFormState();
  const [category] = useCookies(['myCategory']);
  const [superuser] = useCookies(['mySuperuser']);
  const [cookies] = useCookies(['myToken']);
  const [inputs, setInputs] = useState(initialFormState);
  const [inProgress, setInProgress] = useState(false);
  const [dcGroup,setDcGroup]=useState([]);
  const [SelectedDcGroup,setSelectedDcGroup]=useState({});
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingModalTitle, setPendingModalTitle] = useState('');
  const [pendingAgeDays, setPendingAgeDays] = useState(null);
  const [plants, setPlants] = React.useState([]);
  const [csrRows, setCsrRows] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);  
  const [todayDueCounts, setTodayDueCounts] = useState({ day7: 0, day28: 0 });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingOpenedRef = React.useRef(false);

  const loadReportNumberForPlant = (plantId, reportDate, { silent = false } = {}) => {
    if (!plantId) {
      return;
    }
    const reportDateParam = reportDate ? getDisplayDate(reportDate) : undefined;
    getCubeTestReportNumber(cookies, plantId, reportDateParam)
      .then((response) => {
        setIsLoading(false);
        setInputs((values) => ({
          ...values,
          plant_id: plantId,
          csr_num_prefix: `${response.prefix}${response.cube_test_report_no}`,
          csr_no: response.cube_test_report_no,
          prefix: response.prefix,
        }));
      })
      .catch((error) => {
        setIsLoading(false);
        setInputs((values) => ({
          ...values,
          plant_id: plantId,
          csr_num_prefix: '',
          csr_no: '',
          prefix: '',
        }));
        if (!silent) {
          displayError(error.response?.data, 'Failed');
        }
      });
  };

  const getCastingDateForAge = (age) => {
    const reportDate = inputs.csr_date
      ? new Date(`${inputs.csr_date}T00:00:00`)
      : new Date();
    reportDate.setDate(reportDate.getDate() - age);
    const year = reportDate.getFullYear();
    const month = String(reportDate.getMonth() + 1).padStart(2, '0');
    const day = String(reportDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayCastingDateForAge = (age) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    today.setDate(today.getDate() - age);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const countTodayDueForAge = (list, age) => {
    const dueDateDisplay = getDisplayDate(getTodayCastingDateForAge(age));
    return (list || []).filter((item) => item.delivery_challan_date === dueDateDisplay).length;
  };

  const refreshTodayDueCounts = (plantId) => {
    if (!plantId) {
      setTodayDueCounts({ day7: 0, day28: 0 });
      return;
    }
    const casting7 = getTodayCastingDateForAge(7);
    const casting28 = getTodayCastingDateForAge(28);
    Promise.all([
      getDeliveryChallanListForCTR(cookies, plantId, getDisplayDate(casting7), null, 7),
      getDeliveryChallanListForCTR(cookies, plantId, getDisplayDate(casting28), null, 28),
    ])
      .then(([response7, response28]) => {
        setTodayDueCounts({
          day7: countTodayDueForAge(response7?.delivery_challan_list, 7),
          day28: countTodayDueForAge(response28?.delivery_challan_list, 28),
        });
      })
      .catch(() => {
        setTodayDueCounts({ day7: 0, day28: 0 });
      });
  };

  React.useEffect(() => {
    refreshTodayDueCounts(inputs.plant_id);
  }, [inputs.plant_id, cookies]);

  const summaryCards = useMemo(() => [
    buildGradientCard("Today 7th Due", todayDueCounts.day7, 'today'),
    buildGradientCard("Today 28th Due", todayDueCounts.day28, 'red'),
  ], [todayDueCounts]);

  const applyDcGroupToForm = (selectedDcGroup, ageDays) => {
    const castingDt = castingDateInputFromApi(selectedDcGroup.delivery_challan_date);
    const reportDate = reportDateFromCastingAndAge(castingDt, ageDays);
    const cubeId = buildCubeIdFromSelection(selectedDcGroup, ageDays, castingDt);
    setSelectedDcGroup(selectedDcGroup);
    setInputs((values) => ({
      ...values,
      dc_group_id: selectedDcGroup.id,
      casting_dt: castingDt,
      csr_date: reportDate || values.csr_date,
      pending_age_days: ageDays,
      cured_place: values.cured_place || 'plant',
      no_of_moulds: 0,
    }));
    setRowsData(syncCubeIdsForAgeRows(
      [buildPendingAgeRow(ageDays, cubeId, 'plant')],
      selectedDcGroup,
      castingDt,
    ));
    setCsrRows([]);
    if (inputs.plant_id && reportDate) {
      loadReportNumberForPlant(inputs.plant_id, reportDate);
    }
  };

  const openPendingList = (ageDays) => {
    if (!inputs.plant_id) {
      Swal.fire('Please choose plant location first.', '', 'info');
      return;
    }
    const queryRange = getPendingQueryRange(ageDays);
    setIsLoading(true);
    setPendingAgeDays(ageDays);
    getPendingCubeTestReports(cookies, inputs.plant_id, ageDays, {
      fromDate: queryRange.fromDate.format('DD-MM-YYYY'),
      uptoDate: queryRange.uptoDate.format('DD-MM-YYYY'),
    })
      .then((response) => {
        const tableData = decoratePendingRecords(response.pending_list);
        setDcGroup(tableData);
        setPendingModalTitle(buildPendingListTitle(ageDays, queryRange.fromDate, queryRange.uptoDate));
        setShowPendingModal(true);
        setIsLoading(false);
        if (!tableData.length) {
          Swal.fire(
            `No pending ${ageDays}-day records found for this plant in the last 90 days.`,
            '',
            'info',
          );
        }
      })
      .catch((error) => {
        setIsLoading(false);
        displayError(error.response?.data, ' Failed');
      });
  };

  React.useEffect(() => {
    const pendingAge = parseInt(searchParams.get('pendingAge') || '', 10);
    if ((pendingAge === 7 || pendingAge === 28) && inputs.plant_id && !pendingOpenedRef.current) {
      pendingOpenedRef.current = true;
      setPendingAgeDays(pendingAge);
      openPendingList(pendingAge);
    }
  }, [searchParams, inputs.plant_id]);
  
  const pendingColumns = useMemo(() => getPendingColumns(true), []);

  const generateCubeRowsForAgeRow = async (rowIndex) => {
    if (!inputs.casting_dt || !SelectedDcGroup.grade_number) {
      Swal.fire('Please choose casting date and delivery challan from pending list.', '', 'info');
      return;
    }
    const row = rowsData[rowIndex];
    if (!row) {
      return;
    }
    const moulds = parseInt(row.no_of_moulds, 10) || 0;
    if (!moulds) {
      Swal.fire('Please enter mould count for this age row.', '', 'info');
      return;
    }
    if (!row.cured_place) {
      Swal.fire('Please select mould casting area for this row.', '', 'info');
      return;
    }
    const { merged, cancelled, empty } = await generateAndMergeCsrRowsForAge({
      existingCsrRows: csrRows,
      ageRow: row,
      castingDateInput: inputs.casting_dt,
      mode: 'create',
    });
    if (cancelled) {
      return;
    }
    if (empty) {
      Swal.fire('Could not generate cube rows for this age.', '', 'info');
      return;
    }
    setCsrRows(merged);
    setInputs((values) => ({
      ...values,
      no_of_moulds: merged.length,
      cured_place: resolveCuredPlaceFromAgeRows(rowsData, values.cured_place),
    }));
  };

  const agePanelVisible = Boolean(inputs.casting_dt && SelectedDcGroup.grade_number);

  React.useEffect(() => {
    if (!SelectedDcGroup?.customer_name || !rowsData.length) {
      return;
    }
    setRowsData((current) => syncCubeIdsForAgeRows(current, SelectedDcGroup, inputs.casting_dt));
  }, [SelectedDcGroup?.customer_name, SelectedDcGroup?.product_name, inputs.casting_dt]);

  React.useEffect(() => {
    getUserAllowedPlants(cookies)
    .then (
      plantList => {
        const plantOptions = mapAllowedPlants(plantList);
        setPlants(plantOptions);
        const plantIdFromUrl = searchParams.get('plantId');
        const defaultPlantId = plantIdFromUrl || pickDefaultPlantId(plantOptions);
        if (defaultPlantId) {
          setInputs((values) => ({ ...values, plant_id: defaultPlantId }));
          loadReportNumberForPlant(defaultPlantId, inputs.csr_date, { silent: true });
        } else {
          setIsLoading(false);
        }
      }
    )
    .catch(() => {
      setIsLoading(false);
    });
  }, [cookies, searchParams]);

  const ClosePending = () => {
    setShowPendingModal(false);
  };

  const handlePendingRowSelect = (value) => {
    const selected = dcGroup.find((obj) => obj.id == value);
    if (!selected) {
      return;
    }
    if (selected.pending_type === 'update' && selected.csr_id) {
      setShowPendingModal(false);
      navigate(`/CubeTestReportEdit/${selected.csr_id}?pendingAge=${pendingAgeDays || 28}`, {
        state: {
          pendingAge: pendingAgeDays || 28,
          fromPending: true,
        },
      });
      return;
    }
    applyDcGroupToForm(selected, pendingAgeDays);
    setShowPendingModal(false);
  };

  const handlePendingRowClose = (value) => {
    const selected = dcGroup.find((obj) => obj.id == value);
    if (!selected) {
      return;
    }
    const defaultReason = selected.suggest_close
      ? `Cube test not required — ${selected.product_name || 'low grade'}`
      : `Closed without cube test — ${selected.product_name || 'grade'}`;
    Swal.fire({
      title: 'Close pending report?',
      html: `Mark this pending item as <b>not required</b> / closed.<br/><small>${selected.product_name || ''} · ${selected.customer_name || ''}</small>`,
      input: 'text',
      inputLabel: 'Reason',
      inputValue: defaultReason,
      showCancelButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#b45309',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      const payload = selected.pending_type === 'update' && selected.csr_id
        ? {
            csr_id: selected.csr_id,
            closed_reason: result.value || defaultReason,
          }
        : {
            plant_id: inputs.plant_id,
            casting_dt: selected.delivery_challan_date,
            product_id: selected.product_id,
            consignee_id: selected.consignee_id,
            customer_id: selected.customer_id,
            dc_ids_list: selected.dc_ids_list || [],
            total_dc_qty: selected.total_dc_qty,
            closed_reason: result.value || defaultReason,
          };
      setIsLoading(true);
      closePendingCubeTestReport(cookies, payload)
        .then((response) => {
          Swal.fire(
            'Closed',
            `Report ${response.report_no || ''} marked as not required. Unlock later from Edit if needed.`,
            'success',
          );
          openPendingList(pendingAgeDays || 7);
        })
        .catch((error) => {
          setIsLoading(false);
          displayError(error?.response?.data || error, 'Close Failed');
        });
    });
  };

  const deleteTableRows = (index) => {
    const removed = rowsData[index];
    const rows = [...rowsData];
    rows.splice(index, 1);
    setRowsData(rows);
    if (removed?.age != null && removed.age !== '') {
      setCsrRows((current) => mergeCsrRowsForAge(current, [], removed.age));
    }
  };

  const addTableRows = () => {
    const pendingAge = inputs.pending_age_days || pendingAgeDays || 7;
    const nextAge = resolveNextAgeDays(rowsData, pendingAge);
    const defaultCubeId = buildCubeIdFromSelection(SelectedDcGroup, nextAge, inputs.casting_dt);
    const defaultCuredPlace = rowsData[0]?.cured_place || inputs.cured_place || 'plant';
    setRowsData([
      ...syncCubeIdsForAgeRows(rowsData, SelectedDcGroup, inputs.casting_dt),
      buildPendingAgeRow(nextAge, defaultCubeId, defaultCuredPlace),
    ]);
  }

  const handletableChange = (index, evnt) => {
    const { name, value } = evnt.target;
    setRowsData(applyAgeRowFieldChange(rowsData, index, name, value, SelectedDcGroup, inputs.casting_dt));
  };

  const handleChangeCsr = (index, evnt)=>{
    const { name, value } = evnt.target;
    const CsrRowsInput = [...csrRows];
    CsrRowsInput[index][name] = value;
    if (name==='act_load' || name === 'cus_load'){
      const strength=(value/22.5).toFixed(2);
      const age =parseInt(CsrRowsInput[index]['age']);
      const no_of_rows =CsrRowsInput[index]['no_of_rows'];
      var total = parseFloat(strength);
      var per_csr = age/(4.7+(0.832*age))*100;
      var grade =SelectedDcGroup.grade_number;
      // console.log(grade+"grade");
      var cube_strength = (parseInt(grade)*per_csr/100).toFixed(2);
      CsrRowsInput[index]['ref_cube_strength']=cube_strength;
      CsrRowsInput.map((row,row_index)=>{
        // console.log(JSON.stringify(row)+"row"+row_index+"row_index");
        if((parseInt(row['age']) === age) && (row_index != index)){
          if (name==='act_load'){
          total+= parseFloat(row['act_strength'])
          }
          else{
            total+= parseFloat(row['cus_strength'])
          }
        }
      })
      var average = (total/no_of_rows).toFixed(2);
      CsrRowsInput.map((row_1,row_index_1)=>{
        // console.log(JSON.stringify(row)+"row"+row_index+"row_index");
        if(parseInt(row_1['age']) === age){
          if (name==='act_load'){
            CsrRowsInput[index]['act_strength'] = strength;
            CsrRowsInput[row_index_1]['act_average'] = average;
            CsrRowsInput[row_index_1]['act_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['act_strength']).toFixed(2);
            CsrRowsInput[index]['cus_load'] = value;
            CsrRowsInput[index]['cus_strength'] = strength;
            CsrRowsInput[row_index_1]['cus_average'] = average;
            CsrRowsInput[row_index_1]['cus_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['act_strength']).toFixed(2);
            CsrRowsInput[index]['act_strength_result'] = getCubeStrengthResult(strength, cube_strength);
            CsrRowsInput[index]['cus_strength_result'] = getCubeStrengthResult(strength, cube_strength);
            CsrRowsInput[row_index_1]['act_result'] = getCubeStrengthResult(average, cube_strength);
            CsrRowsInput[row_index_1]['cus_result'] = getCubeStrengthResult(average, cube_strength);
        }
        else{
          CsrRowsInput[index]['cus_strength'] = strength;
          CsrRowsInput[row_index_1]['cus_average'] = average;
          CsrRowsInput[row_index_1]['cus_difference']= (parseFloat(average)-CsrRowsInput[row_index_1]['cus_strength']).toFixed(2);
          CsrRowsInput[index]['cus_strength_result'] = getCubeStrengthResult(strength, cube_strength);
          CsrRowsInput[row_index_1]['cus_result'] = getCubeStrengthResult(average, cube_strength);
        }
        }
      })
      // console.log(total+"total");
    }
    else if(name==='act_weight'){
      CsrRowsInput[index]['cus_weight'] = value;
    }
    // console.log(JSON.stringify(CsrRowsInput)+"CsrRowsInput");
    setCsrRows(CsrRowsInput);
  }

  const handleSubmit=(event)=> {
    event.preventDefault();
    console.log(JSON.stringify(inputs)+"inputs submit");
    console.log(JSON.stringify(csrRows)+csrRows.length+"csrRows");
    if (!SelectedDcGroup || !SelectedDcGroup.id) {
            Swal.fire("Please Select Delivery Challan","","warning");
            return;
          }
    if(inputs.no_of_moulds > 0){
      Swal.fire({title: 'Do you want to save?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
          if (result.isConfirmed) { 
            console.log("can submit");
            createCubeTestReport(cookies, {
              plant_id:inputs.plant_id,
              customer_id:SelectedDcGroup.customer_id,
              consignee_id:SelectedDcGroup.consignee_id,
              report_no:inputs.csr_no,
              report_date:getDisplayDate(inputs.csr_date),
              prefix:inputs.prefix,
              casting_dt:getDisplayDate(inputs.casting_dt),
              technician_name:inputs.technician_name?inputs.technician_name:'',
              cement_type:inputs.cement_type?inputs.cement_type:'',
              cured_place:inputs.cured_place?inputs.cured_place:'',
              cement_content:inputs.cement_content?inputs.cement_content:'',
              grade:SelectedDcGroup.product_id,
              no_of_moulds:inputs.no_of_moulds?parseInt(inputs.no_of_moulds):0,
              user_remarks:inputs.user_remarks,
              //dc_ids_list:SelectedDcGroup.dc_ids_list,
          
              dc_ids_list:SelectedDcGroup?.dc_ids_list || [],
              supplied_qty:SelectedDcGroup.total_dc_qty,
              report_list: buildReportListPayload(csrRows, {
                mode: 'create',
                formatDate: getDisplayDate,
              }),
            })
            .then(response => {
                Swal.fire("Saved!", "", "success");
                Reload();
            }).catch((error) => {
                displayError(error.response.data,"Saved Failed");
            });
          } 
          else if (result.isDismissed) 
          {    
            Swal.fire('Not saved', '', 'info')  
          }
        });
    }
    else{
      Swal.fire('No of moulds must be greater than 0', '', 'info')  
    }
  }
        
  const Reload = () => {
    window.location.reload();
  }
    
  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if (name === 'plant_id') {
      setInputs((values) => ({
        ...values,
        plant_id: value,
        casting_dt: '',
        dc_group_id: '',
      }));
      setSelectedDcGroup({});
      setRowsData([]);
      setCsrRows([]);
      if (value) {
        loadReportNumberForPlant(value, inputs.csr_date);
      } else {
        setInputs((values) => ({ ...values, csr_no: '', prefix: '', csr_num_prefix: '' }));
      }
      refreshTodayDueCounts(value);
      return;
    }
    setInputs(values => ({ ...values, [name]: value }));
  }
  
  const Cancel = () => {
    setInputs(getInitialCubeTestReportFormState());
    setRowsData([]);
    setDcGroup([]);
    setCsrRows([]);
    setSelectedDcGroup({});
    setTodayDueCounts({ day7: 0, day28: 0 });
    pendingOpenedRef.current = false;
  }
   
  const view = () => {
    navigate('/CubeTestReportTable')
  } 

  const Back = () => {
    navigate('/Home')
  }
      
  const showDcDetails = () => {
    if (SelectedDcGroup?.customer_name) {
      showDcGroupPop(SelectedDcGroup);
    }
  }
   
  const handleDcDetails = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    if(name === "creation_date"){
        setInputs(values => ({...values, 'csr_date': event.target.value}))
    } 
    else {
        setInputs(values => ({...values, [name]: event.target.value}))
    }
  }
      
  const updateCsrNumberAndPrefix = (prefix, serial_no) => {
    setInputs(values => ({...values,
        ['csr_num_prefix']:(prefix+serial_no),
        ['csr_no']:serial_no,
        ['prefix']:prefix}));
  }

  return (
        <>
          <div id="outer-container" className="App" >
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <LoadingOverlay inProgress={inProgress}/>
            <div id="page-wrap">
              <form onSubmit={handleSubmit} >
                <div className="premium-page-header-wrap">
              <PremiumPageHeader
                module="QUALITY CONTROL"
                title="Compressive Strength Report"
                subtitle="Select plant and casting date, choose delivery challans, then enter cube test results."
                modeLabel="ADD MODE"
                headerActions={summaryCards.map((card) => (
                  <GradientCard
                    key={card.label}
                    card={card}
                    onClick={() => openPendingList(card.label.includes('7th') ? 7 : 28)}
                  />
                ))}
              />
            </div>
                
                <FloatingControls tableLink="/CubeTestReportTable" onCancel={Cancel} enableCancel={true}/>
                <div className="form-content-wrap">
                    <CubeTestReportSelectionBanner selection={SelectedDcGroup} />
                    <Row className="form-equal-height-row no-gutters">
                      <Col xl={6} lg={12} md={12}>
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                          <label htmlFor="plant_id" className="form-group col-sm-4 text-right">Plant Location</label>
                          <select id="plant_id" name="plant_id" className="form-control col-sm-7 browser-default custom-select mandatory-form-control" required onChange={handleChange} value={inputs.plant_id || ""}>
                            <option value="">Select Plant</option>
                            {plants.map((item) => (
                              <option key={item.value} value={item.value}>
                                                  {item.label}</option>
                            ))}
                          </select><br />

                          <label className="form-group col-sm-4 text-right">Pending Tests</label>
                          <div className="form-group col-sm-7" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openPendingList(7)}
                            >
                              7 Days Pending
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openPendingList(28)}
                            >
                              28 Days Pending
                            </button>
                          </div>
                          <br />

                          <div className="modal" tabIndex="-1" style={{ display: showPendingModal ? 'block' : 'none' }}>
                            <div className="modal-dialog modal-xl" style={{height: "500px"}} >
                              <div className="modal-content">
                                  <div className="modal-body display">
                                    <div className="container item-list-table-container">
                                      <ReactTablePopUpSubmitWrapper
                                          title={pendingModalTitle || 'Pending Cube Tests'}
                                          columns={pendingColumns}
                                          data={dcGroup}
                                          onRowSubmit={handlePendingRowSelect}
                                          onRowClose={handlePendingRowClose}
                                          isLoading={isLoading}
                                          onClose={ClosePending}
                                      />
                                    </div> 
                                  </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      </Col>
                  
                      <Col xl={6} lg={12} md={12}>
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel form-panel-fill">
                          <NumberSetting 
                            handleDateChange={handleDcDetails} 
                            serial_no={inputs.csr_no} 
                            creation_date={inputs.csr_date}
                            creation_time={inputs.csr_time}
                            prefix={inputs.prefix}
                            company_id={inputs.plant_id}
                            voucher_type={"compressive_strength_report"}
                            handleNumberAndPrefixUpdate={updateCsrNumberAndPrefix}
                            cookies={cookies}
                            serial_no_title={'Report No'}
                            hideTime={true}
                            disableAutoFetch={true}/>

                          {SelectedDcGroup.dc_details?.length ? (
                            <>
                              <label className="form-group col-sm-4 text-right"></label>
                              <button
                                type="button"
                                onClick={showDcDetails}
                                style={{
                                  background: 'none',
                                  color: 'blue',
                                  border: 'none',
                                  padding: '0',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                }}
                              >
                                DELIVERY SUMMARY
                              </button>
                            </>
                          ) : null}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mx-0">
                      <Col xl={12} lg={12} md={12} className="px-0">
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3 form-control-panel form-cube-meta-panel w-100">
                          <Row className="form-fields-grid-2x2 mx-0 w-100">
                            <Col md={6} className="px-2">
                              <label htmlFor="casting_dt" className="form-group col-sm-4 text-right">Casting Date</label>
                              <input
                                id="casting_dt"
                                type="date"
                                className="form-control col-sm-8 mandatory-form-control"
                                name="casting_dt"
                                required
                                readOnly
                                title="Auto-filled when you select a row from the pending list"
                                style={{ backgroundColor: inputs.casting_dt ? '#fff' : '#f5f5f5', cursor: 'not-allowed' }}
                                value={inputs.casting_dt || ""}
                              />
                            </Col>
                            <Col md={6} className="px-2">
                              <label htmlFor="cement_type" className="form-group col-sm-4 text-right">Cement Type</label>
                              <input type="text" className="form-control col-sm-8" id="cement_type" name="cement_type" onChange={handleChange} value={inputs.cement_type || ""} />
                            </Col>
                            <Col md={6} className="px-2">
                              <label htmlFor="cement_content" className="form-group col-sm-4 text-right">Cem Content</label>
                              <input type="text" className="form-control col-sm-8" id="cement_content" name="cement_content" onChange={handleChange} value={inputs.cement_content || ""} />
                            </Col>
                            <Col md={6} className="px-2">
                              <label htmlFor="technician_name" className="form-group col-sm-4 text-right">Technician Name</label>
                              <input type="text" className="form-control col-sm-8" name="technician_name" id="technician_name" onChange={handleChange} value={inputs.technician_name || ""} />
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>

                    <CubeTestReportAgePanel
                      visible={agePanelVisible}
                      rowsData={rowsData}
                      noOfMouldsCasted={inputs.no_of_moulds}
                      onAddRow={addTableRows}
                      onDeleteRow={deleteTableRows}
                      onChangeRow={handletableChange}
                      onGenerateRow={generateCubeRowsForAgeRow}
                    />

                    {csrRows.length > 0 && (
                    <>
                    <div className="form-row  shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue container table-responsive">
                      <table className="table-bordered table-responsive searchTable table order-list" id="myTable" style={{textAlign:"center",borderRadius:"10px",backgroundColor:"RGB(188,232,253)"}}>
                        <thead>
                          <tr>
                              <th   style={{verticalAlign:"middle"}}>Sl No</th>
                              <th  style={{verticalAlign:"middle", minWidth:"220px", whiteSpace:"nowrap"}}>Cube Id</th>
                              <th  style={{verticalAlign:"middle"}}>Age</th>
                              <th  style={{verticalAlign:"middle"}}>Testing Date</th>
                              <th  style={{verticalAlign:"middle"}}>Weight(gms)</th>
                              <th  style={{verticalAlign:"middle"}}>Dimension(mm)</th>
                              <th  style={{verticalAlign:"middle"}}>Area mm2</th>
                              <th  style={{verticalAlign:"middle"}}>Reference Value</th>
                              <th  style={{verticalAlign:"middle"}}>Max Load(kn)</th>
                              <th  style={{verticalAlign:"middle"}}>Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle"}}>Average Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle",display:"None"}}>Difference</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Weight(gms)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Max Load(kn)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Strength(mpa)</th>
                              <th  style={{verticalAlign:"middle"}}>Customer Average Strength2(mpa)</th>
                              <th  style={{verticalAlign:"middle",display:"None"}}>Customer Difference</th>
                              <th  style={{verticalAlign:"middle"}}>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          <TableRowsCubeTestReport csrRows={csrRows}  handleChangeCsr={handleChangeCsr}   />
                        </tbody>
                      </table>
                    </div> 

                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 bg-blue form-control-panel" >
                      <label htmlFor="user_remarks" className="form-group col-sm-3 text-center">Remarks</label>
                      <textarea rows="2" cols="50" className="form-control col-sm-9" id="user_remarks" name="user_remarks" onChange={handleChange} value={inputs.user_remarks || ""}></textarea>
                    </div>  
                    </>
                    )}
                </div>
                    
                <div className="footer text-center">
                  {/* {((superuser['mySuperuser']) || (category['myCategory'][0].Is_cubetestreport_for_so_add === true)) &&( */}
                      <Button  type="submit" className="btn btn-twitter" style={{width:"80px",fontWeight:"bold"}} >Save</Button> 
                  {/* )}&nbsp;&nbsp; */}
                  {/* {((superuser['mySuperuser']) || (category['myCategory'][0].Is_cubetestreport_for_so_add === true)) &&( */}
                      <Button type="reset" className="btn btn-twitter"  style={{width:"80px",fontWeight:"bold"}} onClick={Cancel}>Cancel</Button>
                  {/* )}&nbsp;&nbsp; */}
                  {/* {((superuser['mySuperuser']) || (category['myCategory'][0].Is_cubetestreport_for_so_view === true)|| (category['myCategory'][0].Is_cubetestreport_for_so_edit === true) || (category['myCategory'][0].Is_cubetestreport_for_so_delete === true)) &&(   */}
                      <Button className="btn btn-twitter"   type="button"style={{width:"80px",fontWeight:"bold"}} onClick={view}>View</Button>
                  {/* )}&nbsp;&nbsp; */}
                      <Button className="btn btn-twitter"   type="button"style={{width:"80px",fontWeight:"bold"}} onClick={Back}>Home</Button>
                </div>
              </form>
            </div>
          </div>
        </>
  );
}
export default CubeTestReport;
